import { KeyboardEvent, ReactElement, useState } from 'react';
import { Box, Checkbox, Chip, TextField } from '@mui/material';

import { ValueType } from '../../../types/UserSettings';

interface Props {
    value: unknown;
    onChange: (value: unknown) => void;
    disabled?: boolean;
    error?: string;
    valueType?: ValueType;
}

type Kind = 'bool' | 'number' | 'intArray' | 'strArray' | 'object' | 'string';

const resolveKind = (valueType: ValueType | undefined, value: unknown): Kind => {
    switch (valueType) {
        case 'bool':
            return 'bool';
        case 'int':
        case 'float':
            return 'number';
        case 'set[int]':
            return 'intArray';
        case 'set[str]':
            return 'strArray';
        case 'dict[str, int]':
        case 'dict[str, list[str]]':
        case 'dict[str, dict]':
            return 'object';
        case 'str':
        case 'unknown':
            return 'string';
    }
    // valueType === undefined → infer from value
    if (typeof value === 'boolean') return 'bool';
    if (typeof value === 'number') return 'number';
    if (Array.isArray(value)) {
        return value.length === 0 || typeof value[0] === 'number' ? 'intArray' : 'strArray';
    }
    if (typeof value === 'object' && value !== null) return 'object';
    return 'string';
};

interface VariantProps {
    value: unknown;
    onChange: (value: unknown) => void;
    disabled?: boolean;
    error?: string;
}

function BoolInput({ value, onChange, disabled }: Readonly<VariantProps>): ReactElement {
    const checked = typeof value === 'boolean' ? value : false;
    return <Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />;
}

function NumberInput({ value, onChange, disabled, error }: Readonly<VariantProps>): ReactElement {
    return (
        <TextField
            type="number"
            value={typeof value === 'number' ? value : ''}
            onChange={(e) => onChange(Number(e.target.value))}
            disabled={disabled}
            size="small"
            error={!!error}
            helperText={error}
        />
    );
}

interface ArrayInputProps extends VariantProps {
    isIntArray: boolean;
}

function ArrayInput({ value, onChange, disabled, isIntArray }: Readonly<ArrayInputProps>): ReactElement {
    const [chipInput, setChipInput] = useState('');
    const safeValue: unknown[] = Array.isArray(value) ? value : [];

    const handleAdd = (raw: string) => {
        const trimmed = raw.trim();
        if (!trimmed) return;
        if (isIntArray) {
            const n = Number(trimmed);
            if (!Number.isInteger(n)) return;
            onChange([...(safeValue as number[]), n]);
        } else {
            onChange([...(safeValue as string[]), trimmed]);
        }
        setChipInput('');
    };
    const handleDelete = (idx: number) => {
        onChange(safeValue.filter((_, i) => i !== idx));
    };
    const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            handleAdd(chipInput);
        }
    };
    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                alignItems: 'center',
                border: '1px solid',
                borderColor: 'grey.400',
                borderRadius: 1,
                p: 0.5,
                minHeight: 40
            }}
        >
            {safeValue.map((v, i) => (
                <Chip
                    key={String(v)}
                    label={String(v)}
                    onDelete={disabled ? undefined : () => handleDelete(i)}
                    size="small"
                />
            ))}
            {!disabled && (
                <input
                    value={chipInput}
                    onChange={(e) => setChipInput(e.target.value)}
                    onKeyDown={handleKey}
                    placeholder={isIntArray ? 'Add integer...' : 'Add value...'}
                    style={{ border: 'none', outline: 'none', flexGrow: 1, minWidth: 80, fontSize: 14 }}
                />
            )}
        </Box>
    );
}

function ObjectInput({ value, onChange, disabled, error }: Readonly<VariantProps>): ReactElement {
    let jsonStr = '';
    try {
        jsonStr = JSON.stringify(value, null, 2);
    } catch {
        jsonStr = '';
    }
    const handleChange = (raw: string) => {
        try {
            onChange(JSON.parse(raw));
        } catch {
            // keep invalid string as-is for display; parent validates
        }
    };
    return (
        <TextField
            multiline
            rows={4}
            defaultValue={jsonStr}
            onChange={(e) => handleChange(e.target.value)}
            disabled={disabled}
            size="small"
            error={!!error}
            helperText={error || 'Must be valid JSON'}
            fullWidth
        />
    );
}

function StringInput({ value, onChange, disabled, error }: Readonly<VariantProps>): ReactElement {
    return (
        <TextField
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            size="small"
            error={!!error}
            helperText={error}
            fullWidth
        />
    );
}

function ValueInput({ value, onChange, disabled, error, valueType }: Readonly<Props>): ReactElement {
    const kind = resolveKind(valueType, value);
    switch (kind) {
        case 'bool':
            return <BoolInput value={value} onChange={onChange} disabled={disabled} />;
        case 'number':
            return <NumberInput value={value} onChange={onChange} disabled={disabled} error={error} />;
        case 'intArray':
        case 'strArray':
            return (
                <ArrayInput value={value} onChange={onChange} disabled={disabled} isIntArray={kind === 'intArray'} />
            );
        case 'object':
            return <ObjectInput value={value} onChange={onChange} disabled={disabled} error={error} />;
        default:
            return <StringInput value={value} onChange={onChange} disabled={disabled} error={error} />;
    }
}

export default ValueInput;
